import React, { useState } from 'react';
import { Box, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { Check, DarkMode, LightMode } from '@mui/icons-material';
import { useTheme as useThemeMode } from '../../context/ThemeContext';
import { buildTheme } from '../../theme/buildTheme';
import { DARK_TONES, DarkToneName } from '../../theme/refinedDark';
import { barIconButtonSx } from './barStyles';

const LIGHT = buildTheme('light').palette;

/** A tiny page: the canvas, a card on it, and the accent. */
const Swatch: React.FC<{ canvas: string; surface: string; border: string; accent: string }> = ({ canvas, surface, border, accent }) => (
  <Box sx={{ width: 40, height: 28, flexShrink: 0, borderRadius: '7px', bgcolor: canvas, border: `1px solid ${border}`, p: '5px', display: 'flex' }}>
    <Box sx={{ flex: 1, borderRadius: '4px', bgcolor: surface, border: `1px solid ${border}`, display: 'flex', alignItems: 'flex-end', p: '3px' }}>
      <Box sx={{ width: 12, height: 4, borderRadius: 2, bgcolor: accent }} />
    </Box>
  </Box>
);

type Choice = { key: string; label: string; hint: string; swatch: React.ReactNode; active: boolean; pick: () => void };

/** Light, or dark in one of its tones. */
const AppearanceMenu: React.FC = () => {
  const { mode, setMode, darkTone, setDarkTone } = useThemeMode();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const close = () => setAnchor(null);

  const choices: Choice[] = [
    {
      key: 'light',
      label: 'Light',
      hint: 'Bright and airy',
      swatch: <Swatch canvas={LIGHT.background.default} surface={LIGHT.background.paper} border={LIGHT.divider} accent={LIGHT.primary.main} />,
      active: mode === 'light',
      pick: () => setMode('light'),
    },
    ...(Object.keys(DARK_TONES) as DarkToneName[]).map((name) => {
      const t = DARK_TONES[name];
      return {
        key: name,
        label: `Dark · ${t.label}`,
        hint: t.hint,
        swatch: <Swatch canvas={t.canvas} surface={t.surface} border={t.border} accent={t.accent.main} />,
        active: mode === 'dark' && darkTone === name,
        pick: () => { setDarkTone(name); setMode('dark'); },
      };
    }),
  ];

  return (
    <>
      <Tooltip title="Appearance">
        <IconButton onClick={(e) => setAnchor(e.currentTarget)} sx={barIconButtonSx} aria-haspopup="menu" aria-expanded={Boolean(anchor)}>
          {mode === 'dark' ? <DarkMode /> : <LightMode />}
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 1, minWidth: 248, borderRadius: '14px' } } }}
        MenuListProps={{ sx: { py: 0.75 } }}
      >
        <Typography sx={{ px: 2, pt: 0.5, pb: 1, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.disabled' }}>
          Appearance
        </Typography>
        {choices.map((c) => (
          <MenuItem
            key={c.key}
            selected={c.active}
            onClick={() => { c.pick(); close(); }}
            sx={{ mx: 0.75, px: 1.25, py: 0.875, gap: 1.5, borderRadius: '8px' }}
          >
            {c.swatch}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: c.active ? 600 : 500 }}>{c.label}</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{c.hint}</Typography>
            </Box>
            <Check sx={{ fontSize: 18, color: 'primary.main', visibility: c.active ? 'visible' : 'hidden' }} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default AppearanceMenu;
